import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import GlobalNav from "../components/GlobalNav";
import API from "../api";
import genPlayer from "../audio/GenPlayer";
import audioBufferToWav from "audiobuffer-to-wav";
import { toast } from "sonner";
import { Trash, Play, Pause, FloppyDisk, DownloadSimple, SpinnerGap, MusicNote, CloudArrowUp } from "@phosphor-icons/react";

export default function ProfilePage() {
  const { user, ready, updateProfile, logout } = useAuth();
  const nav = useNavigate();
  const [igHandle, setIgHandle] = useState("");
  const [username, setUsername] = useState("");
  const [beats, setBeats] = useState([]);
  const [savedBeats, setSavedBeats] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  // v10.3 — Subscription state (always fetched, section always rendered)
  const [subStatus, setSubStatus] = useState(null); // {has_access, reason, expires_at}
  const [subLoading, setSubLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (ready && !user) nav("/");
  }, [ready, user, nav]);

  useEffect(() => {
    if (user) {
      setIgHandle(user.instagram_handle || "");
      setUsername(user.username || "");
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    API.get("/beats/mine")
      .then((r) => setBeats(r.data.beats || []))
      .catch(() => setBeats([]));
    API.get("/saved-beats")
      .then((r) => setSavedBeats(r.data.beats || []))
      .catch(() => setSavedBeats([]));
    // v9 — DAW cloud projects. Distinct from `beats` (older AI beat lab) and
    // `saved-beats` (AI-generated captures). These are FULL DAW session
    // snapshots the user can click to reload into the arranger.
    setProjectsLoading(true);
    API.get("/projects")
      .then((r) => setProjects(r.data.projects || []))
      .catch(() => setProjects([]))
      .finally(() => setProjectsLoading(false));
    // v10.3 — Fetch DAW subscription status for the always-visible
    // Subscription section. Never gates the section itself — free users
    // see a "No active plan" message instead of a hidden UI.
    setSubLoading(true);
    API.get("/billing/daw-access")
      .then((r) => setSubStatus(r.data))
      .catch(() => setSubStatus({ has_access: false, reason: "no_subscription", expires_at: null }))
      .finally(() => setSubLoading(false));
  }, [user]);

  const cancelSubscription = async () => {
    if (cancelling) return;
    if (!window.confirm("Cancel your Producer Package subscription? Your DAW access will remain active until the end of your current billing period.")) return;
    setCancelling(true);
    try {
      await API.post("/billing/cancel-subscription");
      // Re-fetch so the UI reflects the cancelled state.
      const { data } = await API.get("/billing/daw-access");
      setSubStatus(data);
      toast.success("Subscription cancelled — DAW access remains active until the billing period ends.");
    } catch (e) {
      toast.error("Cancel failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setCancelling(false);
    }
  };

  const openProject = async (projectId) => {
    try {
      const { data } = await API.get(`/projects/${projectId}`);
      // Stash the project in sessionStorage so DAWPage can pick it up on
      // mount without prop-drilling through the router.
      sessionStorage.setItem("crow_daw_pending_project", JSON.stringify(data.project));
      // v11.4 — ALSO put the id in the URL. Redundant with sessionStorage
      // but critical: if a browser extension / hard-refresh / router
      // remount clears sessionStorage, the URL still tells DAWPage the
      // exact project to hydrate. Prevents the "clicked TestA, got TestB"
      // regression Donnell reported.
      nav(`/daw?projectId=${encodeURIComponent(projectId)}`);
    } catch (e) {
      toast.error("Failed to load project: " + (e?.response?.data?.detail || e.message));
    }
  };
  const deleteProject = async (projectId) => {
    if (!window.confirm("Delete this project? This can't be undone.")) return;
    try {
      await API.delete(`/projects/${projectId}`);
      setProjects((rows) => rows.filter((x) => x.id !== projectId));
      toast.success("Project deleted");
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Stop preview when the user leaves the page.
  useEffect(() => {
    return () => { try { genPlayer.stop?.(); } catch { /* noop */ } };
  }, []);

  const togglePreview = async (b) => {
    try {
      if (previewingId === b.id) {
        genPlayer.stop?.();
        setPreviewingId(null);
        return;
      }
      // Switch beats — stop whatever's playing, then load + play.
      genPlayer.stop?.();
      await genPlayer.loadPattern(b.pattern, b.bpm || 120);
      await genPlayer.play();
      setPreviewingId(b.id);
    } catch (e) { toast.error("Preview failed: " + e.message); }
  };

  const exportBeat = async (b) => {
    if (exportingId) return;
    setExportingId(b.id);
    try {
      // Stop any preview so the offline render isn't competing with realtime.
      if (previewingId) { genPlayer.stop?.(); setPreviewingId(null); }
      await genPlayer.loadPattern(b.pattern, b.bpm || 120);
      const buf = await genPlayer.renderToBuffer({ sampleRate: 44100 });
      if (!buf || typeof buf.getChannelData !== "function") throw new Error("Render returned no AudioBuffer");
      const wav = audioBufferToWav(buf);
      const blob = new Blob([wav], { type: "audio/wav" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(b.name || "beat").replace(/[^a-z0-9_-]+/gi, "_")}.wav`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Exported "${b.name}" · ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
    } catch (e) { toast.error("Export failed: " + (e?.message || "unknown")); }
    finally { setExportingId(null); }
  };

  const deleteSavedBeat = async (b) => {
    if (!window.confirm(`Delete "${b.name}"?`)) return;
    try {
      if (previewingId === b.id) { genPlayer.stop?.(); setPreviewingId(null); }
      await API.delete(`/saved-beats/${b.id}`);
      setSavedBeats((rows) => rows.filter((x) => x.id !== b.id));
      toast.success("Beat deleted");
    } catch (e) { toast.error("Delete failed: " + (e?.response?.data?.detail || e.message)); }
  };

  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({ instagram_handle: igHandle.trim().replace(/^@/, ""), username: username.trim() });
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "linear-gradient(180deg,#070707,#0d0d0d)" }}>
      <header className="px-6 py-4 flex items-center justify-between border-b border-[#1a1a1a]">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/")} className="text-sm font-mono text-white/60 hover:text-white" data-testid="profile-back">← back</button>
          <GlobalNav />
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-lg">My Profile</h1>
        <button onClick={logout} className="text-sm text-red-300/80 hover:text-red-300" data-testid="profile-logout">Log out</button>
      </header>

      <main className="max-w-3xl mx-auto p-6 space-y-6">
        <section
          className="rounded-2xl p-6 flex items-center gap-5"
          style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }}
          data-testid="profile-card"
        >
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-extrabold"
            style={{ background: "linear-gradient(135deg,#ec4899,#8b5cf6)", boxShadow: "0 0 18px rgba(236,72,153,0.35)" }}
          >
            {(user.username || user.email).slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="text-xs font-mono text-white/40 uppercase tracking-wider">Profile</div>
            <div className="text-2xl font-display font-extrabold tracking-tight" data-testid="profile-username">{user.username || user.email}</div>
            <div className="text-sm text-pink-300/80 font-mono" data-testid="profile-ig">@{user.instagram_handle || "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">Beats made</div>
            <div className="text-3xl font-extrabold" data-testid="profile-beats-count">{user.beats_count ?? 0}</div>
          </div>
        </section>

        <section className="rounded-2xl p-6 space-y-3" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }}>
          <h2 className="font-display font-bold text-sm tracking-tight">Edit profile</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/40 mb-1 block font-mono">Display name</span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 rounded-md text-sm bg-[#0a0a0a] border border-[#262626] focus:outline-none focus:border-white/40 text-white"
                data-testid="profile-edit-username"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/40 mb-1 block font-mono">Instagram handle</span>
              <input
                value={igHandle}
                onChange={(e) => setIgHandle(e.target.value)}
                placeholder="yourhandle"
                className="w-full px-3 py-2 rounded-md text-sm bg-[#0a0a0a] border border-[#262626] focus:outline-none focus:border-white/40 text-white"
                data-testid="profile-edit-ig"
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 rounded-md text-sm font-semibold"
              style={{ background: "linear-gradient(180deg,#fff,#d4d4d4)", color: "#0a0a0a", border: "1px solid #303030" }}
              data-testid="profile-save"
            >
              {saving ? "saving…" : "Save"}
            </button>
            {savedAt && <span className="text-xs text-emerald-400">Saved ✓</span>}
          </div>
        </section>

        {/* v10.3 — Subscription section. ALWAYS rendered so the Cancel
            control is discoverable even before the user upgrades. Free
            users see a "No active plan" state; subscribers see the
            active window + a Cancel button. */}
        <section
          className="rounded-2xl p-6 space-y-3"
          style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }}
          data-testid="profile-subscription-section"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2">
              <CloudArrowUp size={14} weight="fill" /> Producer Package · Subscription
            </h2>
            <span
              className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded"
              style={{
                background: subStatus?.has_access ? "rgba(34,197,94,0.14)" : "rgba(255,255,255,0.05)",
                color: subStatus?.has_access ? "#4ade80" : "#a3a3a3",
                border: `1px solid ${subStatus?.has_access ? "#16a34a55" : "#2a2a2a"}`,
              }}
              data-testid="profile-subscription-status"
            >
              {subLoading ? "loading…"
                : subStatus?.reason === "admin" ? "admin · full access"
                : subStatus?.has_access ? "active"
                : subStatus?.reason === "expired" ? "expired"
                : "no active plan"}
            </span>
          </div>

          {subLoading ? (
            <div className="text-xs text-white/40 font-mono py-4" data-testid="profile-subscription-loading">
              checking subscription…
            </div>
          ) : subStatus?.reason === "admin" ? (
            <div className="text-sm text-white/60" data-testid="profile-subscription-admin">
              You have unlimited admin access. Producer Package billing does not apply to your account.
            </div>
          ) : subStatus?.has_access ? (
            <div className="space-y-3">
              <div className="text-sm text-white/70">
                Your Producer Package is <span className="text-emerald-400 font-semibold">active</span>.
                {subStatus.expires_at && (
                  <> Access remains until <span className="font-mono text-white">{new Date(subStatus.expires_at).toLocaleDateString()}</span>.</>
                )}
              </div>
              <button
                onClick={cancelSubscription}
                disabled={cancelling}
                className="px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50"
                style={{
                  background: "linear-gradient(180deg,#3f1d1d,#2a1010)",
                  color: "#fecaca",
                  border: "1px solid #7f1d1d",
                }}
                data-testid="profile-cancel-subscription"
              >
                {cancelling ? "Cancelling…" : "Cancel Subscription"}
              </button>
              <p className="text-[11px] text-white/40 font-mono">
                You&apos;ll keep DAW access until the current billing period ends. No further charges will occur.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="profile-subscription-none">
              <div className="text-sm text-white/60">
                {subStatus?.reason === "expired"
                  ? "Your Producer Package has expired. Renew below to keep making music in the C.R.O.W. DAW."
                  : "You don't have an active Producer Package. Upgrade to unlock the full C.R.O.W. DAW."}
              </div>
              <button
                onClick={() => nav("/daw")}
                className="px-4 py-2 rounded-md text-sm font-bold"
                style={{
                  background: "linear-gradient(180deg,#a855f7,#7c3aed)",
                  color: "#fff",
                  border: "1px solid #a855f7",
                  boxShadow: "0 0 14px rgba(168,85,247,0.35)",
                }}
                data-testid="profile-upgrade-subscription"
              >
                Upgrade — Producer Package · $9.99/mo
              </button>
            </div>
          )}
        </section>

        {/* Projects — full DAW session snapshots (v9). Click a card to
            re-hydrate the DAW with that session and pick up where you
            left off. */}
        <section className="rounded-2xl p-6 space-y-3" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }} data-testid="profile-projects-section">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2">
              <FloppyDisk size={14} weight="fill" /> Projects · DAW sessions
            </h2>
            <span className="text-xs text-white/40 font-mono" data-testid="profile-projects-count">{projects.length} saved</span>
          </div>
          {projectsLoading ? (
            <div className="text-sm text-white/40 py-8 text-center font-mono">loading…</div>
          ) : projects.length === 0 ? (
            <div className="text-sm text-white/40 py-8 text-center font-mono" data-testid="profile-projects-empty">
              no DAW projects yet — open the DAW, click the <CloudArrowUp size={11} weight="fill" className="inline" /> Cloud Save button, and give your session a name.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="profile-projects-grid">
              {projects.map((p) => (
                <div
                  key={p.id}
                  className="rounded-xl p-4 flex flex-col gap-2 cursor-pointer transition group"
                  style={{ background: "linear-gradient(135deg,#111,#0a0a0a)", border: "1px solid #262626" }}
                  onClick={() => openProject(p.id)}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#4f4f4f"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#262626"; }}
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm truncate flex-1" data-testid={`project-card-${p.id}-name`}>{p.name}</div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteProject(p.id); }}
                      className="text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
                      title="Delete project"
                      data-testid={`project-card-${p.id}-delete`}
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                    <span className="px-1.5 py-0.5 rounded bg-white/5 text-white/60">{p.bpm} BPM</span>
                    {p.genre && <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(249,115,22,0.15)", color: "#fdba74" }}>{p.genre}</span>}
                    <span className="text-white/40 ml-auto">{new Date(p.updated_at || p.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Library — saved AI Beat Lab generations. The DAW (Coming Soon) */}
        {/* has its own project list under `beats` which we keep loading but */}
        {/* hide for now until the DAW is public. */}
        <section className="rounded-2xl p-6 space-y-3" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }} data-testid="profile-library-section">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2">
              <MusicNote size={14} weight="fill" /> Library · saved beats
            </h2>
            <span className="text-xs text-white/40 font-mono" data-testid="profile-library-count">{savedBeats.length} saved</span>
          </div>

          {savedBeats.length === 0 ? (
            <div className="text-sm text-white/40 py-8 text-center font-mono" data-testid="profile-library-empty">
              no beats saved yet — head to the AI Beat Lab, generate a beat, then tap <FloppyDisk size={11} weight="fill" className="inline" /> Save.
            </div>
          ) : (
            <ul className="divide-y divide-[#1a1a1a]" data-testid="profile-library-list">
              {savedBeats.map((b) => {
                const isPlaying = previewingId === b.id;
                const isExporting = exportingId === b.id;
                return (
                  <li key={b.id} className="py-3 flex items-center justify-between gap-3" data-testid={`library-row-${b.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate" data-testid={`library-row-${b.id}-name`}>{b.name}</div>
                      <div className="text-xs text-white/40 font-mono">
                        {b.bpm ? `${b.bpm} BPM · ` : ""}{b.genre ? `${b.genre} · ` : ""}{new Date(b.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => togglePreview(b)}
                        className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
                        style={{
                          background: isPlaying ? "linear-gradient(180deg,#22c55e,#16a34a)" : "rgba(255,255,255,0.05)",
                          color: isPlaying ? "#0b1f10" : "#e7e7e7",
                          border: `1px solid ${isPlaying ? "#22c55eaa" : "#2a2a2a"}`,
                        }}
                        data-testid={`library-row-${b.id}-preview`}
                      >
                        {isPlaying ? <><Pause size={12} weight="fill" /> Stop</> : <><Play size={12} weight="fill" /> Preview</>}
                      </button>
                      <button
                        onClick={() => exportBeat(b)}
                        disabled={!!exportingId}
                        className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                        style={{ background: "rgba(99,102,241,0.12)", color: "#a5b4fc", border: "1px solid #6366f155" }}
                        data-testid={`library-row-${b.id}-export`}
                      >
                        {isExporting ? <><SpinnerGap size={12} weight="bold" className="animate-spin" /> Rendering…</> : <><DownloadSimple size={12} weight="bold" /> Export</>}
                      </button>
                      <button
                        onClick={() => deleteSavedBeat(b)}
                        className="p-1.5 rounded-md hover:bg-red-500/20 text-red-400"
                        title="Delete from library"
                        data-testid={`library-row-${b.id}-delete`}
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* DAW projects — pre-existing list, kept around so existing saved */}
        {/* DAW projects don't vanish, but de-emphasized while the DAW is */}
        {/* still "Coming Soon" for public users. */}
        {beats.length > 0 && (
          <section className="rounded-2xl p-6 space-y-3" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }}>
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-sm tracking-tight">DAW projects</h2>
              <span className="text-xs text-white/40 font-mono">{beats.length} saved</span>
            </div>
            <ul className="divide-y divide-[#1a1a1a]" data-testid="profile-beats-list">
              {beats.map((b) => (
                <li key={b.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm">{b.title}</div>
                    <div className="text-xs text-white/40 font-mono">{b.bpm} BPM · {new Date(b.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-xs text-white/60 font-mono">
                    {b.is_public ? `🌍 ${b.likes || 0} likes · ${b.plays || 0} plays` : "🔒 private"}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
